import { useMemo } from "react";
import api from "../api";
import { enumLabel, type PlatformEnumOption } from "./enumLabel";

const ENUM_CACHE_SEC = 60 * 60 * 24;

export interface TimezoneOption {
  value: string;
  label: string;
  abbreviation: string;
}

export const platformEnumsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getRunStatuses: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/runs/statuses/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getRunTriggerTypes: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/runs/trigger-types/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getIntegrationStepStages: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/integrations/steps/stages/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getCredentialTypes: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/credentials/types/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getConnectionTypes: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/connections/types/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getExecutionTargets: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/integrations/execution-targets/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getTimezones: builder.query<TimezoneOption[], void>({
      query: () => ({
        url: "/integration-platform/integrations/timezones/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),

    getWebhookProviders: builder.query<PlatformEnumOption[], void>({
      query: () => ({
        url: "/integration-platform/webhook-triggers/providers/",
        method: "GET",
      }),
      keepUnusedDataFor: ENUM_CACHE_SEC,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRunStatusesQuery,
  useGetRunTriggerTypesQuery,
  useGetIntegrationStepStagesQuery,
  useGetCredentialTypesQuery,
  useGetConnectionTypesQuery,
  useGetExecutionTargetsQuery,
  useGetTimezonesQuery,
  useGetWebhookProvidersQuery,
} = platformEnumsApi;

export function usePlatformEnumLabels() {
  const { data: statuses } = useGetRunStatusesQuery();
  const { data: stages } = useGetIntegrationStepStagesQuery();
  const { data: triggers } = useGetRunTriggerTypesQuery();

  return useMemo(
    () => ({
      statusLabel: (value: string | null | undefined) =>
        enumLabel(statuses, value),
      stageLabel: (value: string | null | undefined) =>
        enumLabel(stages, value),
      triggerLabel: (value: string | null | undefined) =>
        enumLabel(triggers, value),
    }),
    [statuses, stages, triggers],
  );
}
